import { useState } from 'react';

import SiteHeader from '../components/SiteHeader';
import { apiErrorMessage, requestMagicLink } from '../lib/api';

/**
 * Request a sign-in link.
 *
 * THE CONFIRMATION IS DELIBERATELY NON-COMMITTAL. The server answers identically
 * whether or not the address has an account, so this screen must too: it says a link is
 * on its way "if there's an account for that email" and never "check your inbox, we
 * sent it". Saying the latter would turn the sign-in form into an oracle for who is a
 * customer -- the exact leak the backend is built to avoid.
 *
 * For the same reason there is no "no account found" path and no offer to sign up.
 */
export default function SignIn() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting || email.trim().length === 0) return;

    setSubmitting(true);
    setError(null);

    try {
      const { message } = await requestMagicLink(email.trim());
      setSentMessage(message);
    } catch (caught: unknown) {
      // Only a malformed address can land here; the server does not distinguish known
      // from unknown addresses.
      setError(apiErrorMessage(caught, 'We could not send that link. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col">
      <SiteHeader />

      <main className="mx-auto w-full max-w-lg px-5 py-14 sm:px-8 sm:py-20 flex-1 min-w-0">
        <h1 className="mm-display">Sign in</h1>
        <p className="mm-lede mt-4">
          We&rsquo;ll email you a link to sign in. No password needed.
        </p>

        {sentMessage ? (
          <div className="mm-panel mt-8 p-5 sm:p-6" data-testid="magic-link-sent">
            <p className="mm-note mm-note--success">
              <span>{sentMessage}</span>
            </p>
            <p className="mm-help mt-4">
              The link expires shortly and can only be used once. If it doesn&rsquo;t arrive,
              check your spam folder, then try again.
            </p>
            <button
              type="button"
              className="mm-btn-secondary mt-4"
              onClick={() => {
                setSentMessage(null);
                setError(null);
              }}
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            // noValidate on purpose: the server is the authority on what a usable email
            // address is, and it already returns a precise message. Letting the browser
            // reject first would produce two different wordings for the same mistake,
            // and a native bubble we cannot style or announce consistently.
            noValidate
            className="mm-panel mt-8 p-5 sm:p-6"
          >
            <label htmlFor="sign-in-email" className="mm-label">
              Email
            </label>
            <p className="mm-help mt-1.5">The address you used when you placed your order.</p>

            <input
              id="sign-in-email"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              className="mm-input mt-3"
            />

            {error && (
              <p role="alert" data-testid="sign-in-error" className="mm-note mm-note--danger mt-3">
                <span>{error}</span>
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || email.trim().length === 0}
              data-testid="request-link"
              className="mm-btn-primary mt-5"
            >
              {submitting ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
